/**
 * ChatOptionsConfirm
 *
 * Single or multiple choice options for main chat (clarifying questions).
 * Fits chat UI: compact, border-border, primary accent. On confirm, sends
 * selected label(s) as the next user message.
 *
 * Primary responsibilities:
 * - Render question + option list (single = one select, multiple = multi-select)
 * - Confirm button sends payload string for onConfirm
 */

'use client'

import React, { useState } from 'react'
import { cn } from '@/lib/utils'
import type { OptionsSuggestion } from '@/types/nexus-interactive-suggestions'

export interface ChatOptionsConfirmProps {
  suggestion: OptionsSuggestion
  onConfirm: (payload: string) => void
  disabled?: boolean
}

export function ChatOptionsConfirm({
  suggestion,
  onConfirm,
  disabled
}: ChatOptionsConfirmProps) {
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const multiple = suggestion.multiple === true

  const toggle = (id: string) => {
    if (disabled) return
    setSelected((prev) => {
      const next = new Set(prev)
      if (multiple) {
        if (next.has(id)) next.delete(id)
        else next.add(id)
      } else {
        next.clear()
        next.add(id)
      }
      return next
    })
  }

  const handleConfirm = () => {
    if (disabled) return
    const labels = suggestion.options
      .filter((o) => selected.has(o.id))
      .map((o) => o.label)
    if (labels.length === 0) return
    const payload = labels.join(', ')
    onConfirm(payload)
  }

  const canConfirm = selected.size > 0

  return (
    <div
      className="border-border bg-muted/30 mt-2 max-w-full rounded-xl border p-3 text-sm"
      data-testid="chat-options-confirm"
    >
      {suggestion.question && (
        <p className="text-foreground/90 mb-2">{suggestion.question}</p>
      )}
      <div className="flex flex-col gap-1.5">
        {suggestion.options.map((opt) => {
          const isSelected = selected.has(opt.id)
          return (
            <button
              key={opt.id}
              type="button"
              disabled={disabled}
              onClick={() => toggle(opt.id)}
              className={cn(
                'flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-left transition-colors',
                isSelected
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border hover:border-muted-foreground/40 hover:bg-muted/50'
              )}
            >
              {multiple ? (
                <span
                  className={cn(
                    'flex size-4 shrink-0 items-center justify-center rounded border',
                    isSelected ? 'border-primary bg-primary' : 'border-muted-foreground/50'
                  )}
                >
                  {isSelected && (
                    <svg className="text-primary-foreground size-2.5" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M2 6l3 3 5-6" />
                    </svg>
                  )}
                </span>
              ) : (
                <span
                  className={cn(
                    'flex size-4 shrink-0 rounded-full border-2',
                    isSelected ? 'border-primary' : 'border-muted-foreground/50'
                  )}
                >
                  {isSelected && (
                    <span className="bg-primary m-0.5 block size-3 rounded-full" />
                  )}
                </span>
              )}
              <span className="flex-1 truncate">{opt.label}</span>
            </button>
          )
        })}
      </div>
      <button
        type="button"
        disabled={disabled || !canConfirm}
        onClick={handleConfirm}
        className={cn(
          'mt-3 w-full rounded-lg border-2 px-4 py-2 text-sm font-medium transition-opacity disabled:opacity-50',
          canConfirm && !disabled
            ? 'border-primary bg-primary/10 text-primary'
            : 'border-border text-muted-foreground'
        )}
      >
        Confirm
      </button>
    </div>
  )
}
